<template>
  <v-dialog persistent v-model="showDialog" content-class="verify-code-dialog col-xs-12 col-sm-10 col-md-4 col-lg-4">
    <v-overlay :value="showLoader">
        <v-progress-circular 
            indeterminate color="primary"
            :size="70" :width="7"
        ></v-progress-circular>
    </v-overlay>
    <v-card class="verify-modal">
      <div class="verify-close-container">
        <v-btn icon @click="onClose">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>
      <div class="verify-content">
        <v-alert
          class="mb-2 mt-1"
          v-if="errorMessage"
          :type="'error'">
          {{ errorMessage }}
        </v-alert>
        <h1 class="mt-3 mb-3">Enter the code you've received</h1>
        <p>
          The code will be sent to {{verificationTypeDescription}}<br/>
          <strong>Please, wait a couple of minutes to receive the code before asking for a new one.</strong>
          You have {{verificationTypeTimeoutDescription}} and 3 attempts to enter the correct code.<br/>
        </p>
        <p v-if="!!infoMessage">{{infoMessage}}</p>
        <v-text-field
          label="Verification code"
          v-model="verificationCode"
          type="number"
          />
        <v-btn
          v-if="!challengeId"
          large
          plain
          color="primary"
          v-on:click="sendVerificationCode"
          ><u>Resend code</u></v-btn>
        <v-btn
          :disabled="!verificationCode"
          large
          color="primary"
          v-on:click="checkCode"
          >{{text}}</v-btn>
      </div>
    </v-card>
  </v-dialog> 
</template>


<script lang="ts">
import Vue from "vue";
import { UserInfo } from "@/types/User";
import codeVerificationService, { VerificationType } from "@/services/codeVerificationService";

export default Vue.extend({
  name: "VerifyCodeDialog",
  props: {
    buttonText: {type: String},
    showDialog: {type: Boolean},
    verificationType: {type: Number},
    contactInfo: {type: String},
    onSuccess: {type: Function},
    onClose: {type: Function},
    challengeId: {type: String, default: ""},
    onVerify: {type: Function},
  },
  data: function() {
    return {
      text: this.buttonText ?? "Verify",
      errorMessage: "",
      infoMessage: "",
      showLoader: false,
      verificationGuid: "",
      verificationGuids: [],
      verificationCode: "",
    };
  },
  watch: {
    "showDialog": function(newVal, oldVal){
      if (newVal === oldVal) return;

      if (newVal === true) {
        this.verificationCode = "";
        if (!this.challengeId) this.sendVerificationCode();
      }
    }
  },
  computed: {
    userInfo(): UserInfo {
      return this.$store.state.userInfo;
    },
    verificationTypeTimeoutDescription(): String {
      switch(this.verificationType) {
        case VerificationType.Email:
        case VerificationType.PhoneNumber:
        default:
          return "30 minutes";
      }
    },
    verificationTypeDescription(): String {
      switch(this.verificationType) {
        case VerificationType.Email:
          return "the email address provided.";
        case VerificationType.PhoneNumber:
          return "the phone number provided.";
        default:
          return "";
      }
    },
    sendCodeFun(): Function {
      switch(this.verificationType) {
        case VerificationType.Email:
          return codeVerificationService.startEmailVerification;
        case VerificationType.PhoneNumber:
          return codeVerificationService.startPhoneNumberVerification;
        default:
          return () => {};
      }
    },
  }, 
  methods: {
    sendVerificationCode: function() {   
      this.sendCodeFun(this.contactInfo)
        .then((res: any) => {
          if (res.success) {
            this.verificationGuid = res.instanceId;
            this.verificationGuids = res.instanceIds;
            this.verificationCode = "";
            this.errorMessage = "";
            this.infoMessage = res.infoMessage ?? "";
          } else if (res.errors) {
            this.errorMessage = res.errors.join(",");
          }
        })
        .catch(this.$handleError);
    },
    checkCode: async function() {
      this.showLoader = true;

      if (this.challengeId && this.onVerify) {
        try {
          const response = await this.onVerify(this.challengeId, this.verificationCode);
          if (response.success) {
            this.onSuccess(response);
            this.errorMessage = "";
            this.verificationCode = "";
          } else {
            this.errorMessage = response.errorMessage;
          }
        } catch (error) {
          this.errorMessage = "The code could not be verified. Please try again.";
        } finally {
          this.showLoader = false;
        }
        return;
      }

      if (!this.verificationGuid && !this.verificationGuids.length) 
      {
        this.showLoader = false;
        this.errorMessage = "Start verification failed";
      }

      if (!!this.verificationGuid || this.verificationGuids.length === 1) {
        const guid = this.verificationGuid ?? this.verificationGuids[0];

        const response = await codeVerificationService.postVerificationCodeForChallengeType(guid, this.verificationCode, this.verificationType);

        if (response.CodeCorrect) {
          this.onSuccess();
          this.errorMessage = "";
          this.verificationCode = "";
          this.verificationGuid = "";
          this.verificationGuids = [];
        } else this.errorMessage = response.ErrorMessage;

        this.showLoader = false;
      } else {
        let success = false;
        let errorMessage = "";
        let lastError: unknown = null;

        for (const guid of this.verificationGuids) {
          try {
            const response = await codeVerificationService.postVerificationCodeForChallengeType(guid, this.verificationCode, this.verificationType);

            if (response.CodeCorrect == true) {
              success = true;
              break; // No further checks are needed;
            } else errorMessage = response.ErrorMessage;
          } catch (error) {
            lastError = error;
          }
        }
        
        if (success) {
          this.onSuccess();
          this.errorMessage = "";
          this.verificationCode = "";
          this.verificationGuid = "";
          this.verificationGuids = [];
        } else if (errorMessage) {
          this.errorMessage = errorMessage;
        } else if (lastError) {
          this.$handleError(lastError);
        }

        this.showLoader = false;
      }
    },
  },
});
</script>
